//T.32832 - Servicio de configuración para Google Maps - Optimizado para minimizar consumo de API
import { Injectable } from '@angular/core';

/**
 * Configuración centralizada para Google Maps
 * Diseñado para minimizar el consumo del API de Google Maps
 */
@Injectable({
  providedIn: 'root'
})
export class GoogleMapsConfigService {

  //T.32832 - ═══════════════════════════════════════════════════════════
  //T.32832 - CONFIGURACIÓN DE GOOGLE MAPS API
  //T.32832 - ═══════════════════════════════════════════════════════════
  
  /**
   * API Key de Google Maps
   * IMPORTANTE: En producción, esta key debe estar en variables de entorno
   * y restringida por dominio/IP en Google Cloud Console
   */
  private readonly GOOGLE_MAPS_API_KEY = 'AIzaSyA0d_pizeqiPR9OgFRRxieM8MfOTCKsD1U';
  
  /**
   * Configuración del mapa base
   */
  private readonly MAP_CONFIG = {
    // Zoom inicial
    ZOOM_INICIAL: 14,
    
    // Zoom al centrar en un elemento
    ZOOM_CENTRADO: 16,
    
    // Tipo de mapa (roadmap, satellite, hybrid, terrain)
    MAP_TYPE: 'roadmap' as google.maps.MapTypeId,
    
    // Controles del mapa (minimizar para reducir complejidad)
    DISABLE_DEFAULT_UI: false,
    ZOOM_CONTROL: true,
    MAP_TYPE_CONTROL: false,
    SCALE_CONTROL: false,
    STREET_VIEW_CONTROL: false,
    ROTATE_CONTROL: false,
    FULLSCREEN_CONTROL: true,
    
    // Estilos del mapa (opcional - para personalización)
    STYLES: [
      {
        featureType: 'poi',
        elementType: 'labels',
        stylers: [{ visibility: 'off' }]
      },
      {
        featureType: 'poi.business',
        stylers: [{ visibility: 'off' }]
      },
      {
        featureType: 'poi.park',
        elementType: 'labels',
        stylers: [{ visibility: 'off' }]
      },
      {
        featureType: 'poi.medical',
        stylers: [{ visibility: 'off' }]
      },
      {
        featureType: 'poi.school',
        stylers: [{ visibility: 'off' }]
      },
      {
        featureType: 'poi.sports_complex',
        stylers: [{ visibility: 'off' }]
      },
      {
        featureType: 'poi.government',
        stylers: [{ visibility: 'off' }]
      },
      {
        featureType: 'poi.place_of_worship',
        stylers: [{ visibility: 'off' }]
      },
      {
        featureType: 'poi.attraction',
        stylers: [{ visibility: 'off' }]
      }
    ] as google.maps.MapTypeStyle[]
  };

  /**
   * Configuración de intervalos de actualización (en milisegundos)
   * IMPORTANTE: Estos valores determinan la frecuencia de actualización
   * Un valor más alto = menos consumo de recursos
   */
  private readonly UPDATE_INTERVALS = {
    // Actualización de datos desde el backend (para vista administrativa)
    // Por defecto: 3 minuto (60000 ms)
    DATOS_UPDATE_MS: 180000,
    
    // Tiempo de espera antes de reintentar en caso de error
    RETRY_DELAY_MS: 5000
  };

  /**
   * Configuración de intervalos DINÁMICOS basados en distancia a garita
   * Esta estrategia optimiza el consumo de datos móviles y peticiones al backend
   * Mientras más lejos está el vehículo, menos frecuente es la actualización
   * 
   * ESTRUCTURA:
   * - distanciaMaxKm: Distancia máxima para aplicar este intervalo
   * - intervaloMs: Tiempo entre actualizaciones en milisegundos
   * - descripcion: Descripción del rango para documentación
   */
  private readonly INTERVALOS_DINAMICOS = [
    {
      distanciaMaxKm: 10,
      intervaloMs: 120000,      // 2 minutos (120000 ms)
      descripcion: 'Muy cerca de la garita (< 10 km)'
    },
    {
      distanciaMaxKm: 20,
      intervaloMs: 300000,      // 5 minutos (300000 ms)
      descripcion: 'Cerca de la garita (10-20 km)'
    },
    {
      distanciaMaxKm: 50,
      intervaloMs: 600000,      // 10 minutos (600000 ms)
      descripcion: 'Proximidad media (20-50 km)'
    },
    {
      distanciaMaxKm: 100,
      intervaloMs: 900000,      // 15 minutos (900000 ms)
      descripcion: 'Distancia media (50-100 km)'
    },
    {
      distanciaMaxKm: 200,
      intervaloMs: 1800000,     // 30 minutos (1800000 ms)
      descripcion: 'Lejos de la garita (100-200 km)'
    },
    {
      distanciaMaxKm: Infinity,
      intervaloMs: 3600000,     // 60 minutos (3600000 ms)
      descripcion: 'Muy lejos de la garita (> 200 km)'
    }
  ];

  /**
   * Configuración de marcadores
   */
  private readonly MARKER_CONFIG = {
    // Usar marcadores HTML personalizados (más livianos que imágenes)
    USE_CUSTOM_HTML_MARKERS: true,
    
    // Animación de marcadores
    ANIMATION_DROP: false,
    
    // Optimización de marcadores
    OPTIMIZED: true
  };

  //T.32832 - ═══════════════════════════════════════════════════════════
  //T.32832 - MÉTODOS PÚBLICOS
  //T.32832 - ═══════════════════════════════════════════════════════════

  constructor() {
    console.log('T.32832 - GoogleMapsConfigService inicializado');
  }

  /**
   * Obtiene la API Key de Google Maps
   */
  public getApiKey(): string {
    return this.GOOGLE_MAPS_API_KEY;
  }

  /**
   * Obtiene la configuración del mapa
   */
  public getMapConfig() {
    return { ...this.MAP_CONFIG };
  }

  /**
   * Obtiene los intervalos de actualización
   */
  public getUpdateIntervals() {
    return { ...this.UPDATE_INTERVALS };
  }

  /**
   * Obtiene la configuración de intervalos dinámicos
   */
  public getIntervalosDinamicos() {
    return [...this.INTERVALOS_DINAMICOS];
  }

  /**
   * Calcula el intervalo de actualización dinámico basado en la distancia a la garita
   * 
   * @param distanciaMetros - Distancia actual del vehículo a la garita en metros
   * @returns Intervalo en milisegundos para la próxima actualización
   * 
   * ESTRATEGIA DE OPTIMIZACIÓN:
   * - Vehículos lejanos actualizan menos frecuentemente
   * - Vehículos cercanos actualizan más frecuentemente
   * - Reduce consumo de datos móviles y peticiones al backend
   * - Mantiene precisión cuando es necesario (cerca de garita)
   */
  public calcularIntervaloOptimo(distanciaMetros: number): { intervalo: number; descripcion: string } {
    const distanciaKm = distanciaMetros / 1000;
    
    // Buscar el intervalo apropiado según la distancia
    const config = this.INTERVALOS_DINAMICOS.find(
      rango => distanciaKm <= rango.distanciaMaxKm
    );

    // Si no se encuentra (no debería pasar), usar el intervalo más largo
    const intervaloConfig = config || this.INTERVALOS_DINAMICOS[this.INTERVALOS_DINAMICOS.length - 1];

    console.log(`T.32832 - Intervalo dinámico: ${distanciaKm.toFixed(2)} km → ${intervaloConfig.intervaloMs / 1000 / 60} min (${intervaloConfig.descripcion})`);

    return {
      intervalo: intervaloConfig.intervaloMs,
      descripcion: intervaloConfig.descripcion
    };
  }

  /**
   * Obtiene una descripción legible del tiempo de intervalo
   */
  public formatearIntervalo(intervaloMs: number): string {
    const minutos = intervaloMs / 1000 / 60;
    if (minutos < 60) {
      return `${minutos} minuto${minutos !== 1 ? 's' : ''}`;
    }
    const horas = minutos / 60;
    return `${horas} hora${horas !== 1 ? 's' : ''}`;
  }

  /**
   * Obtiene la configuración de marcadores
   */
  public getMarkerConfig() {
    return { ...this.MARKER_CONFIG };
  }

  /**
   * Actualiza el intervalo de actualización de marcadores (en milisegundos)
   * Permite cambiar dinámicamente la frecuencia de actualización
   */
  public setMarcadoresUpdateInterval(milliseconds: number): void {
    if (milliseconds < 1000) {
      console.warn('T.32832 - Intervalo muy pequeño, ajustando a 1 segundo (1000ms)');
      milliseconds = 1000;
    }
    (this.UPDATE_INTERVALS as any).MARCADORES_UPDATE_MS = milliseconds;
    console.log('T.32832 - Intervalo de actualización de marcadores configurado a:', milliseconds, 'ms');
  }

  /**
   * Actualiza el intervalo de actualización de datos (en milisegundos)
   */
  public setDatosUpdateInterval(milliseconds: number): void {
    if (milliseconds < 1000) {
      console.warn('T.32832 - Intervalo muy pequeño, ajustando a 1 segundo (1000ms)');
      milliseconds = 1000;
    }
    (this.UPDATE_INTERVALS as any).DATOS_UPDATE_MS = milliseconds;
    console.log('T.32832 - Intervalo de actualización de datos configurado a:', milliseconds, 'ms');
  }

  /**
   * Genera opciones del mapa para Google Maps
   */
  public getMapOptions(center: google.maps.LatLngLiteral): google.maps.MapOptions {
    return {
      center: center,
      zoom: this.MAP_CONFIG.ZOOM_INICIAL,
      mapTypeId: this.MAP_CONFIG.MAP_TYPE,
      disableDefaultUI: this.MAP_CONFIG.DISABLE_DEFAULT_UI,
      zoomControl: this.MAP_CONFIG.ZOOM_CONTROL,
      mapTypeControl: this.MAP_CONFIG.MAP_TYPE_CONTROL,
      scaleControl: this.MAP_CONFIG.SCALE_CONTROL,
      streetViewControl: this.MAP_CONFIG.STREET_VIEW_CONTROL,
      rotateControl: this.MAP_CONFIG.ROTATE_CONTROL,
      fullscreenControl: this.MAP_CONFIG.FULLSCREEN_CONTROL,
      styles: this.MAP_CONFIG.STYLES
    };
  }

  /**
   * Obtiene el zoom para centrar en un elemento
   */
  public getZoomCentrado(): number {
    return this.MAP_CONFIG.ZOOM_CENTRADO;
  }

  /**
   * Verifica si la API Key está configurada
   */
  public isApiKeyConfigured(): boolean {
    return this.GOOGLE_MAPS_API_KEY.length > 0 &&
           !this.GOOGLE_MAPS_API_KEY.includes('YOUR_GOOGLE_MAPS_API_KEY_HERE');
  }
}
